import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, X } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
export function Home() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-413" data-mp="24j4ugi5cy08o">
      {/* 1) Hero */}
      <section className="relative px-6 pt-36 pb-28 md:pb-32 bg-[#1a2838] overflow-hidden" data-id="element-414" data-mp="29f282gp8kshr">
        {/* Architectural background — sparse vertical rules for structure */}
        <div className="absolute inset-0 pointer-events-none" data-id="element-415" data-mp="3kvk44fox8t3v">
          <div className="absolute left-[12%] top-0 bottom-0 w-px bg-white/[0.04]" data-id="element-416" data-mp="3kxd2z8tam91b" />
          <div className="absolute left-[50%] top-0 bottom-0 w-px bg-white/[0.03]" data-id="element-417" data-mp="3ovaskcqz6x58" />
          <div className="absolute right-[12%] top-0 bottom-0 w-px bg-white/[0.04]" data-id="element-418" data-mp="30vgnona991rl" />
          <div className="absolute top-[45%] left-[8%] right-[8%] h-px bg-white/[0.04]" data-id="element-419" data-mp="2vyq7lzyga666" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_30%_45%,rgba(139,168,159,0.05),transparent)]" data-id="element-420" data-mp="2qxkew24siiwn" />

        <div className="relative w-full max-w-5xl mx-auto z-10 pt-6 md:pt-10" data-id="element-421" data-mp="2mwqqz8rcl43r">
          <div className="max-w-3xl" data-id="element-422" data-mp="2hyp5yiad2upn">
            <motion.h1 initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.22, 1, 0.36, 1]
          }} className="text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.5rem] font-serif font-semibold mb-8 leading-[1.12] tracking-[-0.02em]" data-id="element-423" data-mp="2hzqkwxui01cn">
              <span className="text-white/80" data-id="element-424" data-mp="1kkyzb54u5fom">Growth is working.</span>
              <br data-id="element-425" data-mp="1kmtk8jg3x7sp" />
              <span className="text-white" data-id="element-426" data-mp="1om2fxtupjfqw">
                But you are not capturing all the revenue moving through the
                business.
              </span>
            </motion.h1>

            <motion.div initial="hidden" animate="visible" variants={{
            hidden: {
              opacity: 0
            },
            visible: {
              opacity: 1,
              transition: {
                delayChildren: 0.3,
                staggerChildren: 0.05
              }
            }
          }} className="max-w-[540px] mb-8" data-id="element-427" data-mp="1flmf6s5cmmpq">
              {/* Problem — the cause (staggered list) */}
              <div className="border-l border-seaglass/25 pl-4 py-1 space-y-2.5 mb-6" data-id="element-428" data-mp="2m91gifoit7u2">
                {['The wrong customers get sold.', 'Leads die in the middle.', 'Handoffs break.', 'Delivery cleans up bad promises.', 'Expansion gets missed.'].map((symptom, i) => <motion.p key={i} variants={{
                hidden: {
                  opacity: 0,
                  x: -10
                },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: {
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1]
                  }
                }
              }} className="text-[15px] text-white/60 font-medium" data-id="element-429" data-mp="oenlfcr8jq1h" data-mpi={i}>
                    {symptom}
                  </motion.p>)}
              </div>

              {/* Divider — visual pivot between cause and effect */}
              <motion.div variants={{
              hidden: {
                opacity: 0,
                scaleX: 0
              },
              visible: {
                opacity: 1,
                scaleX: 1,
                transition: {
                  duration: 0.5
                }
              }
            }} className="w-10 h-px bg-seaglass/50 origin-left mb-6" data-id="element-430" data-mp="1c62udodip5lp" />

              {/* Response — the effect */}
              <motion.p variants={{
              hidden: {
                opacity: 0,
                y: 10
              },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1]
                }
              }
            }} className="text-[15px] md:text-[16px] text-white/75 leading-[1.75]" data-id="element-431" data-mp="2vbc0jz67v13j">
                We help companies find where revenue gets stuck, lost, or missed
                across the journey, then redesign the right parts in the right
                order so more of the right revenue converts, sticks, and
                expands.
              </motion.p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.45,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-id="element-432" data-mp="ogrvvlgct3h2">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-3 px-10 py-[18px] bg-sand text-navy text-[15px] font-bold tracking-[0.01em] hover:bg-white transition-all duration-300 w-fit" data-id="element-433" data-mp="24t7033h5f44v">
                <span data-id="element-434" data-mp="3ucp1jp2exbrh">Book an Honest Conversation</span>
                <ArrowRight className="w-4 h-4" data-id="element-435" data-mp="271oyjyc2z0e" />
              </Link>
              <span className="text-[13px] text-white/45 font-medium" data-id="element-436" data-mp="3kkstbl6m459j">
                45 minutes. No pitch. Just clarity.
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2) Recognition */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-437" data-mp="3altqij8j33ug">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-438" data-mp="3floi6p04nxgk">
          <div className="max-w-3xl" data-id="element-439" data-mp="36lt1y5rptc9o">
            <motion.div variants={fadeIn} data-id="element-440" data-mp="36mun532g3f54">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-441" data-mp="zvlhqoqznxy9">
                What looks like a sales problem often isn&apos;t.
              </h2>

              <div className="space-y-4 text-body text-text" data-id="element-442" data-mp="1ewsci24je4lw">
                <p data-id="element-443" data-mp="1ev7j4ftykrpm">
                  A lot of companies are generating demand and still not
                  capturing what they should from it.
                </p>
                <p data-id="element-444" data-mp="1nrgenjetf66h">
                  What looks like a sales issue, an operations issue, or a
                  customer success issue is often a breakdown somewhere between
                  demand and delivery.
                </p>
                <p data-id="element-445" data-mp="1soyrhkvye5jr">
                  The real bottleneck rarely lives neatly inside one department.
                  It usually lives in the seams — where handoffs break, promises
                  drift, opportunities go unseen, and the signal is not clear
                  enough to move with confidence.
                </p>
                <p data-id="element-446" data-mp="c9yk9aj4jiwm">That usually shows up when:</p>
              </div>

              <ul className="space-y-3 pl-1 mt-8 mb-8" data-id="element-447" data-mp="894wqi9lg649">
                {['the wrong customers get sold', 'leads stall in the middle', 'follow-up dies in handoffs', 'delivery inherits bad promises', 'churn starts upstream', 'expansion gets missed', 'pricing or packaging no longer fits the path customers are actually moving through', 'teams keep working around friction instead of removing it'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-448" data-mp="dxabyx70zrdj" data-mpi={i}>
                    <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-449" data-mp="iv3ftjkuw9an" />
                    <span className="text-base text-text font-medium" data-id="element-450" data-mp="mv46q3p56cyl">
                      {item}
                    </span>
                  </li>)}
              </ul>

              <p className="text-lg font-semibold text-navy" data-id="element-451" data-mp="1ya0w5ep8n46h">
                The visible symptom is usually not where the real problem lives.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Revenue Journey Graphic */}
      <section className="py-16 px-6 bg-navy" data-id="element-452" data-mp="28f93b6pa7wur">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-6xl mx-auto" data-id="element-453" data-mp="28fixl6hd0q69">
          <p className="eyebrow text-white/40 mb-6 text-center" data-id="element-454" data-mp="1unb5vt4xnpef">
            The Revenue Journey
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/4XKEhzsa5vVcCQuW3xH9kA/Blueprint_Anatomy_(16_9).png" alt="Revenue journey diagram showing value leaking at each stage — from first interest through qualification, conversion, handoff, delivery, retention, and expansion to customer lifetime value" className="w-full h-auto" data-id="element-455" data-mp="1pnyhv52bsca9" />
        </motion.div>
      </section>

      {/* 3) What we actually change */}
      <section className="py-24 px-6 bg-paper-dark" data-id="element-456" data-mp="3azq4mvmykxuu">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-457" data-mp="cntkxyefyo27">
          <div className="max-w-3xl" data-id="element-458" data-mp="mkohwcv2gged">
            <motion.div variants={fadeIn} className="mb-10" data-id="element-459" data-mp="hn668epnzn2o">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-460" data-mp="hlv5knrimvfs">
                What we actually change
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-6 text-body text-text" data-id="element-461" data-mp="3l2bphq56blh2">
              <p data-id="element-462" data-mp="3v2uh572zc69j">We do not redesign everything.</p>

              <p data-id="element-463" data-mp="3v0zvtrnsqc4x">
                We find the point where better structure will create the most
                leverage, then redesign the right parts around it in the right
                order.
              </p>

              <p data-id="element-464" data-mp="32bsxots97ok7">That can include:</p>

              <ul className="space-y-3 pl-1" data-id="element-465" data-mp="1hvcpbmobga8v">
                {['qualification and fit', 'routing and handoffs', 'conversion logic', 'promise-to-delivery alignment', 'churn drivers that start upstream', 'retention and expansion logic', 'pricing or packaging that no longer fits', 'decision rules and exceptions', 'workflow and team enablement', 'the visibility needed to trust what is happening'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-466" data-mp="1rkmh11f8zqwg" data-mpi={i}>
                    <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-467" data-mp="1wkil10ny3ic0" />
                    <span className="text-text" data-id="element-468" data-mp="g6sfrql3ao0s">{item}</span>
                  </li>)}
              </ul>

              <div className="space-y-3 pt-2" data-id="element-469" data-mp="l5vlzect30ac">
                <p data-id="element-470" data-mp="l3iuoal5rdkm">This is not a giant overhaul.</p>
                <p data-id="element-471" data-mp="2mv17fd5fknxe">It is not more process for the sake of process.</p>
                <p data-id="element-472" data-mp="2hvoj0nywv8sp">It is not another tool rollout.</p>
              </div>

              <p className="text-lg font-semibold text-navy" data-id="element-473" data-mp="2hxrkc45ikocq">
                It is a disciplined way to find the next domino, remove what is
                blocking revenue, and make growth easier to carry.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 4) We start wide, then go deep */}
      <section className="py-24 px-6 bg-paper grain" data-id="element-474" data-mp="1z4nkg11pdf9o">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-475" data-mp="1z79vwbrpsex6">
          <div className="max-w-3xl" data-id="element-476" data-mp="3okeowdjcr5p7">
            <motion.div variants={fadeIn} className="mb-10" data-id="element-477" data-mp="3ojbz68xrn9av">
              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-478" data-mp="3ki9yros44r7b">
                We start wide, then go deep.
              </h2>
            </motion.div>

            <motion.div variants={fadeIn} className="space-y-8" data-id="element-479" data-mp="1b925c6orp7w2">
              <div className="space-y-4 text-body text-text" data-id="element-480" data-mp="1b7ir0lmo96wx">
                <p data-id="element-481" data-mp="1g13q6h0046xp">
                  We take a fast pass across the journey end to end, then zoom
                  in where revenue is actually getting stuck, lost, or missed.
                </p>
                <p data-id="element-482" data-mp="ndh9c71rj5sw">
                  That keeps us from treating the loudest symptom like the
                  source.
                </p>
                <p data-id="element-483" data-mp="wckvjjw881bm">It also keeps us from fixing the wrong thing first.</p>
              </div>

              <div className="border-l-2 border-seaglass/60 pl-8 py-3" data-id="element-484" data-mp="wexi6acxizv0">
                <p className="text-body text-navy font-semibold" data-id="element-485" data-mp="11cz3l1xtvb9n">
                  We are not looking for everything that could be improved.
                </p>
                <p className="text-body text-navy font-semibold mt-2" data-id="element-486" data-mp="2cq1jrvw0lyta">
                  We are looking for the next domino.
                </p>
              </div>

              <p className="text-lg text-navy font-semibold" data-id="element-487" data-mp="26akljfk2sffq">
                Better diagnosis comes before better tools.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 5) When this is a fit */}
      <section className="py-24 px-6 bg-paper" data-id="element-488" data-mp="2g7z2rhdtp893">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-489" data-mp="2u8jj86w9mrzf">
          <motion.div variants={fadeIn} className="mb-12 text-center md:text-left" data-id="element-490" data-mp="33x1hagd5ukzd">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-491" data-mp="2z01kkw27aukx">
              When this is a fit
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-16" data-id="element-492" data-mp="38196sfrg04yn">
            <motion.div variants={fadeIn} data-id="element-493" data-mp="3rlzq54qy2lhk">
              <h3 className="text-xl font-semibold text-navy mb-6" data-id="element-494" data-mp="3rm9dekkluejk">
                This is usually a fit when…
              </h3>
              <ul className="space-y-3" data-id="element-495" data-mp="d2lfk25zpda">
                {['growth is happening, but too much revenue still gets stuck, lost, or missed across the journey', 'problems keep getting created in the handoffs between sales, delivery, customer success, and operations', 'the same issue keeps showing up in different places under different names', 'pricing, packaging, churn, or expansion seem more connected than they first appear', 'leadership can feel the drag, but it is not clear what to fix first', 'better signal, cleaner handoffs, and better rules would matter more than another meeting or tool'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-496" data-mp="2uufmnjkki20l" data-mpi={i}>
                    <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-497" data-mp="10ez29pwqv4ky" />
                    <span className="text-base text-text" data-id="element-498" data-mp="voina1d9zawh">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div variants={fadeIn} className="md:pl-12 md:border-l md:border-seaglass/15" data-id="element-499" data-mp="qq8rppnn28ac">
              <h3 className="text-xl font-semibold text-navy mb-6" data-id="element-500" data-mp="mm0zvjlvwc7w">
                When it isn&apos;t…
              </h3>
              <ul className="space-y-3" data-id="element-501" data-mp="1eei82c2323u3">
                {['you want someone to run operations for you long-term', 'the main problem is raw capacity, not journey friction or missed opportunity', 'you want generic AI consulting or team training', 'you already know exactly what needs to be implemented and just need execution', 'the issue is not meaningfully tied to how revenue moves through the business'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-502" data-mp="25o38lov9jbzi" data-mpi={i}>
                    <X className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-503" data-mp="2aktoab35o5ke" />
                    <span className="text-base text-text" data-id="element-504" data-mp="2engkeo6v0cne">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* 6) Start here */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-505" data-mp="2obyhovfxk1ma">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto text-center md:text-left" data-id="element-506" data-mp="2tc4aiexxru5y">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-507" data-mp="32aooj2sjxfnu">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-508" data-mp="13y7x7yi5is2y">
              Start with an Honest Conversation
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="bg-white border border-seaglass/20 shadow-sm p-8 md:p-12" data-id="element-509" data-mp="p1pw4tpj09wz">
            <p className="text-body text-text mb-8" data-id="element-510" data-mp="k1c0kb542sfu">
              A 45-minute working conversation to pressure-test what you are
              seeing, where revenue may actually be getting stuck, lost, or
              missed, and whether there is a real issue worth diagnosing.
            </p>

            <div className="mb-10 text-left" data-id="element-511" data-mp="65rg3b9qi26b">
              <p className="text-sm font-semibold text-navy mb-4" data-id="element-512" data-mp="153u540d4biy">
                You leave with:
              </p>
              <ul className="space-y-3" data-id="element-513" data-mp="f5egpuun33yb">
                {['a clearer read on what is really going on', 'whether the issue looks structural, situational, or not worth touching right now', 'the next right step, including "keep pushing" if that is the truth'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-514" data-mp="1u1fs4i3dzth2" data-mpi={i}>
                    <Check className="w-5 h-5 text-seaglass flex-shrink-0 mt-0.5" data-id="element-515" data-mp="1u04nyh614jpf" />
                    <span className="text-base text-text" data-id="element-516" data-mp="1y3swp36edlc9">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="text-left" data-id="element-517" data-mp="1p2bl9y3xq7tw">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-518" data-mp="1p3e8nw21ts52">
                <span data-id="element-519" data-mp="2hstjakpsxj0g">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-520" data-mp="231b8d1bsueny" />
              </Link>
              <p className="text-sm text-text-muted mt-4" data-id="element-521" data-mp="280ntnhn0vdof">
                45 minutes. No pitch. Just clarity.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>
    </main>;
}